import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, ArrowRight, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { api } from '../context/AuthContext';

const formatN = (n) => new Intl.NumberFormat('fr-FR').format(n || 0);
const MAX_ATTEMPTS = 12; // ~24s total with 2s interval

/** Extract the reference from the query string (CinetPay only). */
function detectPayment(params) {
  const ref = params.get('transaction_id') || params.get('cpm_trans_id') || params.get('reference') || params.get('trxref') || params.get('token');
  if (ref) return { provider: 'cinetpay', reference: ref };
  return { provider: null, reference: null };
}

export default function PaymentCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { provider, reference } = detectPayment(params);
  const [status, setStatus] = useState('verifying');
  const [donation, setDonation] = useState(null);
  const [cagnotte, setCagnotte] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!reference || !provider) { setStatus('missing'); return undefined; }
    let alive = true;
    let timer;

    const endpoint = `/donations/cinetpay/verify/${reference}`;

    const check = async (n) => {
      try {
        const { data } = await api.get(endpoint);
        if (!alive) return;
        if (data.status === 'success') {
          setStatus('success');
          setDonation(data.donation || null);
          setCagnotte(data.cagnotte || null);
          return;
        }
        if (data.status === 'failed' || data.status === 'abandoned') {
          setStatus('failed');
          return;
        }
        // pending — poll again
        if (n < MAX_ATTEMPTS) {
          timer = setTimeout(() => { setAttempts(n + 1); check(n + 1); }, 2000);
        } else {
          setStatus('pending_timeout');
        }
      } catch (err) {
        if (!alive) return;
        setStatus('error');
      }
    };

    check(0);
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [reference, provider]);

  const goToCagnotte = () => {
    if (cagnotte?.id) navigate(`/cagnotte/${cagnotte.id}`);
    else navigate('/');
  };

  const providerLabel = provider === 'cinetpay' ? 'CinetPay' : 'Paystack';

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10" data-testid="payment-callback-page">
      <div className="w-full max-w-[520px] rounded-3xl bg-white border border-black/5 p-8 sm:p-10 text-center shadow-sm">
        <div className="w-11 h-11 rounded-xl bg-[#1a1d1a] text-white flex items-center justify-center font-bold mx-auto">F</div>

        {status === 'verifying' && (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-neutral-400 mx-auto mt-6" />
            <h1 className="mt-5 text-[22px] font-semibold text-[#1a1d1a]">Vérification du paiement {providerLabel}…</h1>
            <p className="mt-1 text-[13.5px] text-neutral-500">Merci de patienter quelques secondes. Tentative {attempts + 1}/{MAX_ATTEMPTS}.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-14 h-14 rounded-full mx-auto mt-6 flex items-center justify-center bg-emerald-50 text-emerald-600">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h1 className="mt-4 text-[24px] font-semibold text-[#1a1d1a]">Merci pour votre don ❤️</h1>
            {donation && (
              <p className="mt-2 text-[14px] text-neutral-600">
                Votre don de <span className="font-semibold text-[#1a1d1a]">{formatN(donation.amount)} {donation.currency || 'FCFA'}</span> a bien été enregistré via {providerLabel}.
              </p>
            )}
            {cagnotte && (
              <p className="mt-1 text-[13px] text-neutral-500">Cagnotte : {cagnotte.title}</p>
            )}
            <div className="mt-7 flex flex-wrap gap-2 justify-center">
              <Button data-testid="callback-view-cagnotte-btn" onClick={goToCagnotte} className="btn-anim rounded-full bg-[#1a1d1a] hover:bg-black text-white h-11 px-5">
                Voir la cagnotte <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <Button variant="outline" asChild className="btn-anim rounded-full h-11 px-5 border-black/10">
                <Link to="/"><Home className="w-4 h-4 mr-1" /> Accueil</Link>
              </Button>
            </div>
          </>
        )}

        {(status === 'failed' || status === 'error' || status === 'missing') && (
          <>
            <div className="w-14 h-14 rounded-full mx-auto mt-6 flex items-center justify-center bg-rose-50 text-rose-600">
              <XCircle className="w-8 h-8" />
            </div>
            <h1 className="mt-4 text-[22px] font-semibold text-[#1a1d1a]">Paiement non abouti</h1>
            <p className="mt-2 text-[14px] text-neutral-600">
              {status === 'missing' ? 'Aucune référence de paiement reçue.' : 'Votre paiement n\'a pas pu être confirmé. Vous pouvez réessayer.'}
            </p>
            <div className="mt-6 flex justify-center gap-2">
              <Button variant="outline" asChild className="btn-anim rounded-full h-11 px-5 border-black/10">
                <Link to="/"><Home className="w-4 h-4 mr-1" /> Retour à l'accueil</Link>
              </Button>
            </div>
          </>
        )}

        {status === 'pending_timeout' && (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-neutral-400 mx-auto mt-6" />
            <h1 className="mt-4 text-[20px] font-semibold text-[#1a1d1a]">Paiement en cours de validation</h1>
            <p className="mt-2 text-[13.5px] text-neutral-500">Nous attendons la confirmation de l'opérateur. Vous pouvez rafraîchir la page dans quelques instants.</p>
            <Button onClick={() => window.location.reload()} className="mt-5 btn-anim rounded-full bg-[#1a1d1a] hover:bg-black text-white h-11 px-5">Rafraîchir</Button>
          </>
        )}
      </div>
    </div>
  );
}
